// Which i18n key an auth failure should be SHOWN as, or null to fall back to
// whatever the identity provider said.
//
// ⚠️ Exists because `auth.signInFailed` was, on its own, a dead branch. Both
// surfaces render `err.message ?? t("auth.signInFailed")`, and both build that
// error through a `toAuthError` that already defaults `message` to a string
// (apps/web-rnw/src/auth/cognito.ts:56, apps/mobile/src/auth/cognito.ts:64) —
// so the `??` can never fire and the athlete always reads Cognito's own copy.
// Measured in a real browser on the served build, not deduced: a wrong password
// announces "Incorrect username or password." in English, on the one screen an
// athlete who reads in French sees first.
//
// ⛔ Deliberately NOT a full code→copy table. Only the two codes that mean "that
// pair did not authenticate" are mapped, and they map to the SAME string on
// purpose: telling the two apart would let the page enumerate which addresses
// have an account. Every other code (unconfirmed user, expired password, rate
// limit) still shows the provider's message, still in English — that is a real
// residue with its own backlog voice, not something this helper silently
// pretends to have covered.
const CREDENTIAL_FAILURE_CODES = new Set([
  "NotAuthorizedException",
  "UserNotFoundException",
]);

export const signInErrorKey = (code: string | undefined): string | null =>
  code !== undefined && CREDENTIAL_FAILURE_CODES.has(code)
    ? "auth.signInFailed"
    : null;

// ⛔ Una SECONDA tabella, nello stesso file, invece di allargare quella sopra.
// I quattro codici con cui Cognito rifiuta la chiusura di un recupero vogliono
// l'opposto di ciò che `signInErrorKey` garantisce: lì due codici collassano in
// una stringa opaca perché distinguerli direbbe quali indirizzi hanno un
// account; qui il codice è già stato spedito a quell'indirizzo, non c'è niente
// da nascondere, e la copia per-codice è l'unico dettaglio azionabile — «l'hai
// sbagliato» e «è scaduto» chiedono due gesti diversi. Un solo posto per «che
// codice Cognito dice cosa», due tabelle perché sono due contratti di privacy.
//
// I codici sono quelli documentati su `ConfirmForgotPassword`. ⚠️ I tentativi
// li conta Cognito, non noi: ⛔ nessun contatore nel client.
const FORGOT_ERROR_KEYS: Record<string, string> = {
  CodeMismatchException: "auth.forgotCodeWrong",
  ExpiredCodeException: "auth.forgotCodeExpired",
  TooManyFailedAttemptsException: "auth.forgotTooManyAttempts",
  // Due codici diversi per la stessa cosa vista dall'atleta — ha chiesto
  // troppo, riprovi più tardi — e nessuno dei due dice nulla sull'account.
  LimitExceededException: "auth.forgotTooManyRequests",
  TooManyRequestsException: "auth.forgotTooManyRequests",
};

export const forgotPasswordErrorKey = (
  code: string | undefined,
): string | null => (code === undefined ? null : (FORGOT_ERROR_KEYS[code] ?? null));

// Il `code` e il testo di un rigetto Cognito, letti da un `unknown`.
//
// ⛔ Vivono qui e non nei mount perché il codice deve SOPRAVVIVERE fino al form
// condiviso: i due montaggi facevano `throw new Error(message)`, che tiene la
// frase inglese e butta via l'unica cosa da cui si può scegliere una
// traduzione. La forma `{ code, message }` è quella che entrambi i
// `toAuthError` producono già.
export const authErrorCode = (err: unknown): string | undefined => {
  if (err === null || typeof err !== "object") return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
};

export const authErrorText = (err: unknown, fallback: string): string => {
  if (err === null || typeof err !== "object") return fallback;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
};
